import { Grid, Box } from "@mui/material"
import ColumnChart from "component/Chart/ColumnChart";
import TreeMap from "component/Chart/TreeMap";
import { useMemo, useRef, useEffect, useState, useCallback } from "react";
import GaugeChart from "component/GaugeChart";
import { set_p_and_l } from "redux/reducer/user";
import { useDispatch, useSelector } from "react-redux";

const toSafeNumber = (value) => {
    const num = Number(value);
    return Number.isFinite(num) ? num : 0;
};

const DEFAULT_ROW_HEIGHT = 320;
const MIN_ROW_HEIGHT = 280;
const MAX_ROW_HEIGHT = 420;

const clampRowHeight = (value) => {
    const num = Math.floor(value);
    if (!Number.isFinite(num)) return DEFAULT_ROW_HEIGHT;
    return Math.min(Math.max(num, MIN_ROW_HEIGHT), MAX_ROW_HEIGHT);
};

const ChartLayout = ({ crypto, p_and_l }) => {
    const dispatch = useDispatch();
    const category = useSelector(state => state.crypto.category)
    const dataCollection = useMemo(() => {
        if (!category?.length) {
            return null;
        }
        const holdings = crypto || [];
        let temp = {}
        for (let { name, _id } of category) {
            temp[name] = { data: holdings.filter((obj => obj.categoryId == _id)) }
            temp[name].total_invested = temp[name].data.reduce((a, obj) => a + toSafeNumber(obj.invested), 0)
            temp[name].total_value = temp[name].data.reduce((a, obj) => a + toSafeNumber(obj.price) * toSafeNumber(obj.qty), 0)
            temp[name].fib1 = temp[name].data.reduce((a, obj) => a + toSafeNumber(obj.fib1), 0)
            temp[name].grand_total_sell = temp[name].data.reduce((a, obj) => a + toSafeNumber(obj.grand_total_sell), 0)
        }
        return Object.keys(temp).length ? temp : null;
    }, [crypto, category])

    const calculatedData = useMemo(() => {
        let temp = {}
        const allData = crypto || [];
        temp.investment = allData.reduce((a, obj) => a + toSafeNumber(obj.invested), 0);
        temp.grand_total_sell = allData.reduce((a, obj) => a + toSafeNumber(obj.grand_total_sell), 0);
        temp.total_profit_sell = allData.reduce((a, obj) => a + toSafeNumber(obj.total_profit_sell), 0);
        temp.xGain = toSafeNumber(temp.investment) === 0 ? '0.0' : (temp.grand_total_sell / temp.investment).toFixed(1);
        temp.total_value = allData.reduce((a, obj) => a + toSafeNumber(obj.price) * toSafeNumber(obj.qty), 0);
        temp.p_and_l = temp.total_value - temp.investment;
        dispatch(set_p_and_l(temp.p_and_l))
        document.title = `Xgain ${temp.xGain} X`
        return temp
    }, [crypto, dispatch])

    const categoryLabels = useMemo(() => {
        if (!dataCollection) return [];
        return Object.keys(dataCollection).map((str) => {
            const invested = dataCollection[str].total_invested;
            const ratio = invested === 0 ? 0 : dataCollection[str].grand_total_sell / invested;
            return `${str}  ${ratio.toFixed(1)}X`;
        });
    }, [dataCollection]);

    const categorySeries = useMemo(() => {
        if (!dataCollection) return [];
        return Object.values(dataCollection).map((obj) => obj.total_value - obj.total_invested);
    }, [dataCollection]);

    const chartsReady = Boolean(dataCollection);

    const gaugeRef = useRef(null);
    const [rowHeight, setRowHeight] = useState(DEFAULT_ROW_HEIGHT);

    const syncRowHeight = useCallback(() => {
        const node = gaugeRef.current;
        if (!node) return;
        const next = clampRowHeight(node.getBoundingClientRect().height);
        setRowHeight((prev) => (prev === next ? prev : next));
    }, []);

    useEffect(() => {
        if (!chartsReady) return;

        syncRowHeight();
        const observer = new ResizeObserver(() => requestAnimationFrame(syncRowHeight));
        const node = gaugeRef.current;
        if (node) observer.observe(node);
        window.addEventListener('resize', syncRowHeight);

        const timers = [100, 300, 600].map((ms) => setTimeout(syncRowHeight, ms));

        return () => {
            observer.disconnect();
            timers.forEach(clearTimeout);
            window.removeEventListener('resize', syncRowHeight);
        };
    }, [chartsReady, syncRowHeight, calculatedData.investment]);

    const sideColumnSx = {
        flex: '1 1 0',
        minWidth: 0,
        height: rowHeight,
        minHeight: rowHeight,
        maxHeight: rowHeight,
        overflow: 'hidden',
    };

    return (
        <>
            <Box
                sx={{
                    display: { xs: 'none', sm: 'flex' },
                    gap: 1,
                    mt: 1,
                    alignItems: 'flex-start',
                }}
            >
                <Box sx={sideColumnSx}>
                    {chartsReady && (
                        <ColumnChart
                            height={rowHeight}
                            categories={Object.keys(dataCollection)}
                            invested={Object.values(dataCollection).map(obj => obj.total_invested)}
                            worth={Object.values(dataCollection).map(obj => obj.total_value)}
                        />
                    )}
                </Box>

                <Box ref={gaugeRef} sx={{ flex: '1 1 0', minWidth: 0 }}>
                    {chartsReady && (
                        <GaugeChart
                            total_invested={calculatedData.investment}
                            grand_total_sell={calculatedData.grand_total_sell}
                            total_roi={calculatedData.total_profit_sell}
                            total_worth={calculatedData.total_value}
                            profit_loss={calculatedData.p_and_l}
                            xgain={calculatedData.xGain}
                            trim={p_and_l}
                        />
                    )}
                </Box>

                <Box sx={sideColumnSx}>
                    {chartsReady && (
                        <TreeMap
                            height={rowHeight}
                            label={categoryLabels}
                            series={categorySeries}
                        />
                    )}
                </Box>
            </Box>

            <Grid sx={{ padding: 0, display: { sm: 'none' } }} marginTop={1} container>
                <Grid item xs={12}>
                    {chartsReady && (
                        <GaugeChart
                            total_invested={calculatedData.investment}
                            grand_total_sell={calculatedData.grand_total_sell}
                            total_roi={calculatedData.total_profit_sell}
                            total_worth={calculatedData.total_value}
                            profit_loss={calculatedData.p_and_l}
                            xgain={calculatedData.xGain}
                            trim={p_and_l}
                        />
                    )}
                </Grid>
                <Grid item xs={12}>
                    {chartsReady && (
                        <TreeMap
                            height={280}
                            label={categoryLabels}
                            series={categorySeries}
                        />
                    )}
                </Grid>
                <Grid item xs={12}>
                    {chartsReady && (
                        <ColumnChart
                            height={280}
                            categories={Object.keys(dataCollection)}
                            invested={Object.values(dataCollection).map(obj => obj.total_invested)}
                            worth={Object.values(dataCollection).map(obj => obj.total_value)}
                        />
                    )}
                </Grid>
            </Grid>
        </>
    )
}

export default ChartLayout;
